import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CreateShoreTeamRequest,
    ShoreTeam,
    ShoreTeamsRequest,
    ShoreTeamsResponse,
    UpdateShoreTeamRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/shore-team.api.ts';
import {
    createApiShoreTeam,
    deleteApiShoreTeam,
    getApiShoreTeams,
    updateApiShoreTeam,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/shore-team.api.ts';
import { extractError } from '@utils/slice.ts';

export interface ShoreTeamsError {
    message?: string;
    [k: string]: any;
}

export interface ShoreTeamsState {
    error: ShoreTeamsError | null;

    commonShoreTeams: ShoreTeamsResponse | null;
    shoreTeams: ShoreTeam[];
    pagination: CommonTablePaginatedResponse;

    updatedShoreTeam: ShoreTeam | null;
    currentGetRequestId: string | null;

    isGetShoreTeamsPending: boolean;
    isCreateShoreTeamPending: boolean;
    isUpdateShoreTeamPending: boolean;
    isDeleteShoreTeamPending: boolean;
}

const initialState: ShoreTeamsState = {
    error: null,

    commonShoreTeams: null,
    shoreTeams: [],
    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },

    updatedShoreTeam: null,
    currentGetRequestId: null,

    isGetShoreTeamsPending: false,
    isCreateShoreTeamPending: false,
    isUpdateShoreTeamPending: false,
    isDeleteShoreTeamPending: false,
};

export const getShoreTeams = createAsyncThunk<
    ShoreTeamsResponse,
    { clientId: number; tenantAlias: string; payload: ShoreTeamsRequest },
    { rejectValue: ShoreTeamsError }
>('adminCenterShoreTeams/getShoreTeams', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiShoreTeams(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createShoreTeam = createAsyncThunk<
    ShoreTeam,
    { clientId: number; tenantAlias: string; payload: CreateShoreTeamRequest },
    { rejectValue: ShoreTeamsError }
>('adminCenterShoreTeams/createShoreTeam', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    createApiShoreTeam(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateShoreTeam = createAsyncThunk<
    ShoreTeam,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateShoreTeamRequest },
    { rejectValue: ShoreTeamsError }
>('adminCenterShoreTeams/updateShoreTeam', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiShoreTeam(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteShoreTeam = createAsyncThunk<
    ShoreTeam,
    { clientId: number; tenantAlias: string; id: number },
    { rejectValue: ShoreTeamsError }
>('adminCenterShoreTeams/deleteShoreTeam', ({ clientId, tenantAlias, id }, { rejectWithValue }) =>
    deleteApiShoreTeam(clientId, tenantAlias, id)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const shoreTeamsSlice = createSlice({
    name: 'adminCenterShoreTeams',
    initialState,
    reducers: {
        resetShoreTeamsState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(getShoreTeams.pending, (state, action) => {
                state.isGetShoreTeamsPending = true;
                state.error = null;
                state.currentGetRequestId = action.meta.requestId;
            })
            .addCase(getShoreTeams.fulfilled, (state, action) => {
                if (state.currentGetRequestId !== action.meta.requestId) return;

                state.isGetShoreTeamsPending = false;
                state.currentGetRequestId = null;
                const { data, pagination } = action.payload;
                state.commonShoreTeams = action.payload;
                state.shoreTeams = data ?? [];
                state.pagination = pagination;
            })
            .addCase(getShoreTeams.rejected, (state, action) => {
                if (state.currentGetRequestId !== action.meta.requestId) return;

                state.isGetShoreTeamsPending = false;
                state.currentGetRequestId = null;
                state.error = extractError(action);
            })
            .addCase(createShoreTeam.pending, (state) => {
                state.isCreateShoreTeamPending = true;
                state.error = null;
            })
            .addCase(createShoreTeam.fulfilled, (state, action) => {
                state.isCreateShoreTeamPending = false;
                state.updatedShoreTeam = action.payload;
            })
            .addCase(createShoreTeam.rejected, (state, action) => {
                state.isCreateShoreTeamPending = false;
                state.error = extractError(action);
            })
            .addCase(updateShoreTeam.pending, (state) => {
                state.isUpdateShoreTeamPending = true;
                state.error = null;
            })
            .addCase(updateShoreTeam.fulfilled, (state, action) => {
                state.isUpdateShoreTeamPending = false;
                state.updatedShoreTeam = action.payload;
            })
            .addCase(updateShoreTeam.rejected, (state, action) => {
                state.isUpdateShoreTeamPending = false;
                state.error = extractError(action);
            })
            .addCase(deleteShoreTeam.pending, (state) => {
                state.isDeleteShoreTeamPending = true;
                state.error = null;
            })
            .addCase(deleteShoreTeam.fulfilled, (state, action) => {
                state.isDeleteShoreTeamPending = false;
                state.updatedShoreTeam = action.payload;
                state.shoreTeams = state.shoreTeams.filter((team) => team.id !== action.payload.id);
            })
            .addCase(deleteShoreTeam.rejected, (state, action) => {
                state.isDeleteShoreTeamPending = false;
                state.error = extractError(action);
            })
            .addCase(getAdminCenterData.fulfilled, (state, action) => {
                const { shoreTeamResponse } = action.payload;

                if (!shoreTeamResponse) return;

                const { data, pagination } = shoreTeamResponse;
                state.commonShoreTeams = shoreTeamResponse;
                state.shoreTeams = data ?? [];
                state.pagination = pagination;
            });
    },
});

export const { resetShoreTeamsState } = shoreTeamsSlice.actions;
export default shoreTeamsSlice.reducer;
